/**
 * Every Google font the editor offers, in one place.
 *
 * Vive en el paquete del tema y no en el editor porque también pintan con estas
 * fuentes el panel y el worker —los índices públicos del catálogo—, y los tres
 * tienen que pedir a Google exactamente lo mismo.
 *
 * This used to live in three: the picker list, the published-site spec map, and
 * a hand-written stylesheet URL for the canvas iframe. A font added to the
 * picker but missed in the URL appears in the menu and renders as the fallback,
 * which looks like a bug in the font rather than a missing entry — so the two
 * consumers derive from here instead of repeating it.
 *
 * `weights` is omitted for families Google ships in a single weight; asking for
 * weights they do not have makes the request fail for every font in it.
 */
export type GoogleFontDef = {
  name: string
  /** The CSS stack, including the generic family the browser falls back to. */
  css: string
  weights?: string
  category: 'sans' | 'serif' | 'display' | 'handwriting' | 'mono'
}

const SANS_W = '300;400;500;600;700;800'
const SHORT_W = '300;400;500;600;700'

export const GOOGLE_FONTS: readonly GoogleFontDef[] = [
  // ── Sans ──────────────────────────────────────────────────────────────────
  { name: 'Inter', css: "'Inter', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Roboto', css: "'Roboto', sans-serif", weights: '300;400;500;700', category: 'sans' },
  { name: 'Open Sans', css: "'Open Sans', sans-serif", weights: '300;400;600;700', category: 'sans' },
  { name: 'Poppins', css: "'Poppins', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Montserrat', css: "'Montserrat', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Lato', css: "'Lato', sans-serif", weights: '300;400;700', category: 'sans' },
  { name: 'Raleway', css: "'Raleway', sans-serif", weights: SHORT_W, category: 'sans' },
  { name: 'Nunito', css: "'Nunito', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Work Sans', css: "'Work Sans', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Rubik', css: "'Rubik', sans-serif", weights: SHORT_W, category: 'sans' },
  { name: 'DM Sans', css: "'DM Sans', sans-serif", weights: SHORT_W, category: 'sans' },
  { name: 'Mulish', css: "'Mulish', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Quicksand', css: "'Quicksand', sans-serif", weights: SHORT_W, category: 'sans' },
  { name: 'PT Sans', css: "'PT Sans', sans-serif", weights: '400;700', category: 'sans' },
  { name: 'Manrope', css: "'Manrope', sans-serif", weights: '300;400;500;600;700;800', category: 'sans' },
  { name: 'Outfit', css: "'Outfit', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Plus Jakarta Sans', css: "'Plus Jakarta Sans', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Figtree', css: "'Figtree', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Sora', css: "'Sora', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Space Grotesk', css: "'Space Grotesk', sans-serif", weights: '300;400;500;600;700', category: 'sans' },
  { name: 'Karla', css: "'Karla', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Barlow', css: "'Barlow', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Cabin', css: "'Cabin', sans-serif", weights: '400;500;600;700', category: 'sans' },
  { name: 'Josefin Sans', css: "'Josefin Sans', sans-serif", weights: SHORT_W, category: 'sans' },
  { name: 'Archivo', css: "'Archivo', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Urbanist', css: "'Urbanist', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Lexend', css: "'Lexend', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Epilogue', css: "'Epilogue', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Public Sans', css: "'Public Sans', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Red Hat Display', css: "'Red Hat Display', sans-serif", weights: '300;400;500;600;700;800', category: 'sans' },

  // Named repeatedly in 2026 enterprise typography guidance: the Plex family is
  // the safe consulting/fintech pairing, and Fraunces / Instrument Serif give a
  // geometric sans the serif contrast that reads as deliberate.
  { name: 'IBM Plex Sans', css: "'IBM Plex Sans', sans-serif", weights: '300;400;500;600;700', category: 'sans' },

  // ── Condensed / narrow ────────────────────────────────────────────────────
  { name: 'Oswald', css: "'Oswald', sans-serif", weights: SHORT_W, category: 'display' },
  { name: 'Bebas Neue', css: "'Bebas Neue', sans-serif", category: 'display' },
  { name: 'Anton', css: "'Anton', sans-serif", category: 'display' },

  // ── Serif ─────────────────────────────────────────────────────────────────
  { name: 'Playfair Display', css: "'Playfair Display', serif", weights: '400;500;600;700', category: 'serif' },
  { name: 'Merriweather', css: "'Merriweather', serif", weights: SHORT_W, category: 'serif' },
  { name: 'Lora', css: "'Lora', serif", weights: '400;500;600;700', category: 'serif' },
  { name: 'Roboto Slab', css: "'Roboto Slab', serif", weights: SHORT_W, category: 'serif' },
  { name: 'Libre Baskerville', css: "'Libre Baskerville', serif", weights: '400;700', category: 'serif' },
  { name: 'Cormorant Garamond', css: "'Cormorant Garamond', serif", weights: '300;400;500;600;700', category: 'serif' },
  { name: 'EB Garamond', css: "'EB Garamond', serif", weights: '400;500;600;700;800', category: 'serif' },
  { name: 'Crimson Text', css: "'Crimson Text', serif", weights: '400;600;700', category: 'serif' },
  { name: 'Bitter', css: "'Bitter', serif", weights: SANS_W, category: 'serif' },
  { name: 'Zilla Slab', css: "'Zilla Slab', serif", weights: '300;400;500;600;700', category: 'serif' },
  { name: 'Spectral', css: "'Spectral', serif", weights: '300;400;500;600;700;800', category: 'serif' },
  { name: 'Noto Serif', css: "'Noto Serif', serif", weights: SANS_W, category: 'serif' },
  { name: 'Domine', css: "'Domine', serif", weights: '400;500;600;700', category: 'serif' },
  { name: 'Arvo', css: "'Arvo', serif", weights: '400;700', category: 'serif' },
  { name: 'IBM Plex Serif', css: "'IBM Plex Serif', serif", weights: '300;400;500;600;700', category: 'serif' },
  { name: 'Source Serif 4', css: "'Source Serif 4', serif", weights: '300;400;500;600;700', category: 'serif' },
  { name: 'Fraunces', css: "'Fraunces', serif", weights: '300;400;500;600;700', category: 'serif' },
  { name: 'Instrument Serif', css: "'Instrument Serif', serif", category: 'serif' },

  // ── Moda y editorial: las combinaciones de lujo y el selector individual ──
  { name: 'Bodoni Moda', css: "'Bodoni Moda', serif", weights: '400;500;600;700;800;900', category: 'serif' },
  { name: 'Italiana', css: "'Italiana', sans-serif", category: 'sans' },
  { name: 'Prata', css: "'Prata', serif", category: 'serif' },
  { name: 'Marcellus', css: "'Marcellus', serif", category: 'serif' },
  { name: 'Tenor Sans', css: "'Tenor Sans', sans-serif", category: 'sans' },
  { name: 'DM Serif Display', css: "'DM Serif Display', serif", category: 'serif' },
  { name: 'Libre Caslon Display', css: "'Libre Caslon Display', serif", category: 'serif' },
  { name: 'Questrial', css: "'Questrial', sans-serif", category: 'sans' },
  { name: 'Gothic A1', css: "'Gothic A1', sans-serif", weights: SANS_W, category: 'sans' },
  { name: 'Hanken Grotesk', css: "'Hanken Grotesk', sans-serif", weights: SANS_W, category: 'sans' },

  // ── Display ───────────────────────────────────────────────────────────────
  { name: 'Abril Fatface', css: "'Abril Fatface', serif", category: 'display' },
  { name: 'Alfa Slab One', css: "'Alfa Slab One', serif", category: 'display' },
  { name: 'Righteous', css: "'Righteous', sans-serif", category: 'display' },
  { name: 'Comfortaa', css: "'Comfortaa', sans-serif", weights: '300;400;500;600;700', category: 'display' },
  { name: 'Fredoka', css: "'Fredoka', sans-serif", weights: '300;400;500;600;700', category: 'display' },

  // Friendly / graphic: letras gruesas, redondeadas y retro para títulos.
  { name: 'Shrikhand', css: "'Shrikhand', serif", category: 'display' },
  { name: 'Chicle', css: "'Chicle', sans-serif", category: 'display' },
  { name: 'Spicy Rice', css: "'Spicy Rice', serif", category: 'display' },
  { name: 'Modak', css: "'Modak', sans-serif", category: 'display' },
  { name: 'Lilita One', css: "'Lilita One', sans-serif", category: 'display' },
  { name: 'Paytone One', css: "'Paytone One', sans-serif", category: 'display' },
  { name: 'Dela Gothic One', css: "'Dela Gothic One', sans-serif", category: 'display' },
  { name: 'Bagel Fat One', css: "'Bagel Fat One', sans-serif", category: 'display' },
  { name: 'Coiny', css: "'Coiny', sans-serif", category: 'display' },
  { name: 'Bowlby One SC', css: "'Bowlby One SC', sans-serif", category: 'display' },

  // ── Handwriting ───────────────────────────────────────────────────────────
  { name: 'Dancing Script', css: "'Dancing Script', cursive", weights: '400;500;600;700', category: 'handwriting' },
  { name: 'Pacifico', css: "'Pacifico', cursive", category: 'handwriting' },
  { name: 'Caveat', css: "'Caveat', cursive", weights: '400;500;600;700', category: 'handwriting' },
  { name: 'Satisfy', css: "'Satisfy', cursive", category: 'handwriting' },
  /* Cursivas elegantes, de caligrafía: para los títulos grandes de las parejas
     «Cursivas» (`FONT_PAIRS`). Un solo peso: en negrita se deformaban. */
  { name: 'Great Vibes', css: "'Great Vibes', cursive", category: 'handwriting' },
  { name: 'Parisienne', css: "'Parisienne', cursive", category: 'handwriting' },
  { name: 'Allura', css: "'Allura', cursive", category: 'handwriting' },
  { name: 'Pinyon Script', css: "'Pinyon Script', cursive", category: 'handwriting' },
  { name: 'Italianno', css: "'Italianno', cursive", category: 'handwriting' },
  { name: 'Petit Formal Script', css: "'Petit Formal Script', cursive", category: 'handwriting' },

  // ── Mono ──────────────────────────────────────────────────────────────────
  { name: 'JetBrains Mono', css: "'JetBrains Mono', monospace", weights: '300;400;500;700', category: 'mono' },
  { name: 'Source Code Pro', css: "'Source Code Pro', monospace", weights: '300;400;500;700', category: 'mono' },
  { name: 'IBM Plex Mono', css: "'IBM Plex Mono', monospace", weights: '300;400;500;600;700', category: 'mono' },
  { name: 'Space Mono', css: "'Space Mono', monospace", weights: '400;700', category: 'mono' },
]

/** `Plus Jakarta Sans:wght@300;400…` — the shape Google's css2 endpoint wants. */
export function googleFontSpec(font: GoogleFontDef): string {
  const family = font.name.replace(/ /g, '+')
  return font.weights ? `${family}:wght@${font.weights}` : family
}

/**
 * Lo que hay que pedirle a Google para unas cuantas pilas de fuentes.
 *
 * Con los pesos que cada familia tiene DE VERDAD, sacados de este catálogo.
 * Pedirle a Google un peso que la familia no tiene no degrada: rechaza la hoja
 * entera, y la página sale con la fuente de sistema. Es lo que pasaba en los
 * índices del catálogo, que pedían los pesos que usaban los estilos —un 300 de
 * título— sin mirar si la familia los tenía.
 *
 * Una familia que no esté aquí se pide sin pesos, que Google acepta siempre. El
 * nombre se busca entre comillas, que es como viaja en la pila que guarda el
 * editor (`'Inter', sans-serif`); sin comillas es de sistema y no se pide nada.
 */
export function googleFontSpecsFor(stacks: readonly unknown[]): string[] {
  const specs = new Set<string>()
  for (const stack of stacks) {
    if (typeof stack !== 'string') continue
    const match = /'([^']{1,60})'|"([^"]{1,60})"/.exec(stack)
    const family = (match?.[1] ?? match?.[2] ?? '').trim()
    if (!/^[A-Za-z0-9 ]+$/.test(family)) continue
    const font = GOOGLE_FONTS.find((item) => item.name === family)
    specs.add(font ? googleFontSpec(font) : family.replace(/ /g, '+'))
  }
  return [...specs]
}
